import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

function StockPage() {
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Search state
    const [searchTerm, setSearchTerm] = useState('');

    // Lead time filter state
    const [leadTimeFilter, setLeadTimeFilter] = useState('');

    const { hasRole } = useAuth();
    const canFilterByStatus = hasRole(['admin', 'invoice-editor']);
    const isAdmin = hasRole('admin');

    // Status filter state
    const [statusFilter, setStatusFilter] = useState('all');

    // Product Type filter state
    const [productTypeFilter, setProductTypeFilter] = useState('all');

    useEffect(() => {
        fetchProducts();
    }, []); // Only fetch once on mount

    const fetchProducts = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`http://localhost:5000/api/products?status=${statusFilter}`);
            setProducts(response.data);
            setFilteredProducts(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching products:', err);
            setError('Failed to load products. Please make sure the backend server is running.');
        } finally {
            setLoading(false);
        }
    };

    // Filter products based on search, lead time, status, and type
    useEffect(() => {
        let filtered = [...products];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(product =>
                product.productName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Status filter — non-privileged users always see active only
        if (!canFilterByStatus) {
            filtered = filtered.filter(product => product.isActive === true);
        } else if (statusFilter !== 'all') {
            const isActive = statusFilter === 'active';
            filtered = filtered.filter(product => product.isActive === isActive);
        }

        // Product Type filter
        if (productTypeFilter !== 'all') {
            filtered = filtered.filter(product => {
                if (productTypeFilter === 'custom') {
                    return product.productType === 'custom';
                } else {
                    // Website products are those with null or empty productType
                    return !product.productType || product.productType === '';
                }
            });
        }

        // Lead time filter - check if ANY threshold has lead time <= filter value
        if (leadTimeFilter && !isNaN(leadTimeFilter)) {
            const filterDays = parseInt(leadTimeFilter);
            filtered = filtered.filter(product => {
                const hasThresholds = product.threshold1 > 0 || product.threshold2 > 0 || product.threshold3 > 0;
                if (!hasThresholds) {
                    // Default lead time product
                    return product.leadTime != null && product.leadTime <= filterDays;
                }
                // Threshold-based product
                return product.leadTime1 <= filterDays ||
                    product.leadTime2 <= filterDays ||
                    product.leadTime3 <= filterDays;
            });
        }

        setFilteredProducts(filtered);
        setCurrentPage(1); // Reset to first page when filtering
    }, [searchTerm, leadTimeFilter, statusFilter, productTypeFilter, products]);

    // Pagination calculations
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo(0, 0);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const handleToggleActive = async (product) => {
        const newState = !product.isActive;
        const action = newState ? 'activate' : 'deactivate';
        const ok = window.confirm(`Are you sure you want to ${action} ${product.productName}?`);
        if (!ok) return;

        try {
            await axios.put('http://localhost:5000/api/products/update', {
                productCode: product.productCode,
                isActive: newState
            });

            // Update local state
            setProducts(prev => prev.map(p => (p.productCode === product.productCode ? { ...p, isActive: newState } : p)));
            setFilteredProducts(prev => prev.map(p => (p.productCode === product.productCode ? { ...p, isActive: newState } : p)));
        } catch (err) {
            console.error('Failed to update product status:', err);
            alert(err.response?.data?.error || 'Failed to update product status');
        }
    };

    if (loading) {
        return <div className="loading">Loading products...</div>;
    }

    if (error) {
        return (
            <div className="card">
                <div className="error-message">{error}</div>
            </div>
        );
    }

    return (
        <div>
            <h1 className="page-title">Products</h1>

            {/* Search and Filters */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="form-section">
                    <h2 className="form-section-title">Search & Filter Products</h2>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1.5fr', gap: '1rem', alignItems: 'end' }}>
                        {canFilterByStatus && (
                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                                Product Status
                            </label>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                style={{ width: '100%', padding: '0.64rem' }}
                            >
                                <option value="all">All Statuses</option>
                                <option value="active">Active Only</option>
                                <option value="inactive">Inactive Only</option>
                            </select>
                        </div>
                        )}
                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                                Product Type
                            </label>
                            <select
                                value={productTypeFilter}
                                onChange={(e) => setProductTypeFilter(e.target.value)}
                                style={{ width: '100%', padding: '0.64rem' }}
                            >
                                <option value="all">All Types</option>
                                <option value="website">Website</option>
                                <option value="custom">Custom</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                                Max Lead Time
                            </label>
                            <input
                                type="number"
                                placeholder="e.g. 10"
                                value={leadTimeFilter}
                                onChange={(e) => setLeadTimeFilter(e.target.value)}
                                min="0"
                                style={{ width: '100%', padding: '0.62rem' }}
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                                Search
                            </label>
                            <input
                                type="text"
                                placeholder="Product name or code..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ width: '100%', padding: '0.62rem' }}
                            />
                        </div>
                    </div>
                </div>
            </div>


            <div className="card">
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>Product</th>
                                <th>Lead Time</th>
                                <th>Threshold 1</th>
                                <th>Lead Time 1</th>
                                <th>Threshold 2</th>
                                <th>Lead Time 2</th>
                                <th>Threshold 3</th>
                                <th>Lead Time 3</th>
                                <th>&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentProducts.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                                        {searchTerm ? 'No products found matching your search.' : 'No products found. Run the seed script to add sample products.'}
                                    </td>
                                </tr>
                            ) : (
                                currentProducts.map((product) => (
                                    <tr key={product.id} className={product.isActive ? 'active-product' : 'inactive-product'}>
                                        <td style={{ position: 'relative' }}>
                                            {/* Hanging Badge - Top Left */}
                                            <span className={`product-label-hanging product-label-${product.productType}`}>
                                                {product.productType !== 'custom' ? '🌐' : '⚙️'}
                                            </span>
                                            <div className="product-cell">
                                                <img
                                                    src={product.imageUrl}
                                                    alt={product.productName}
                                                    className="product-image"
                                                />
                                                <div>
                                                    <div className="product-name">{product.productName}</div>
                                                    <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                                        {product.zohoId && product.zohoId.trim() !== '' && `CRM Id: ${product.zohoId.trim()}`}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{
                                                fontWeight: '600',
                                                color: '#344ea1'
                                            }}>
                                                {product.leadTime === 9999
                                                    ? 'Contact'
                                                    : (product.leadTime != null ? `${product.leadTime} days` : '-')}
                                            </span>
                                        </td>
                                        <td>{product.threshold1 > 0 ? product.threshold1 : '-'}</td>
                                        <td>{product.threshold1 > 0 ? (product.leadTime1 === 9999 ? 'Contact' : `${product.leadTime1} days`) : '-'}</td>
                                        <td>{product.threshold2 > 0 ? product.threshold2 : '-'}</td>
                                        <td>{product.threshold2 > 0 ? (product.leadTime2 === 9999 ? 'Contact' : `${product.leadTime2} days`) : '-'}</td>
                                        <td>{product.threshold3 > 0 ? product.threshold3 : '-'}</td>
                                        <td>{product.threshold3 > 0 ? (product.leadTime3 === 9999 ? 'Contact' : `${product.leadTime3} days`) : '-'}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            {isAdmin ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleToggleActive(product)}
                                                    title={product.isActive ? 'Deactivate product' : 'Activate product'}
                                                    style={{
                                                        padding: '0.35rem 0.6rem',
                                                        borderRadius: '18px',
                                                        border: 'none',
                                                        background: product.isActive ? '#10b981' : '#ef4444',
                                                        color: 'white',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {product.isActive ? 'Active' : 'Inactive'}
                                                </button>
                                            ) : (
                                                <span style={{ display: 'inline-block', width: '36px', height: '20px', borderRadius: '12px', background: product.isActive ? '#10b981' : '#ef4444' }}></span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Items per page and count */}
            <div className="card" style={{ marginTop: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label style={{ fontWeight: '600' }}>Items per page:</label>
                        <select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            style={{
                                padding: '0.5rem',
                                borderRadius: '6px',
                                border: '1px solid #ddd',
                                cursor: 'pointer'
                            }}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <div style={{ fontWeight: '600', color: '#00aeef' }}>
                        Showing {filteredProducts.length > 0 ? indexOfFirstItem + 1 : 0} to {Math.min(indexOfLastItem, filteredProducts.length)} of {filteredProducts.length} products
                    </div>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="card" style={{ marginTop: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            style={{
                                padding: '0.5rem 1rem',
                                background: currentPage === 1 ? '#ddd' : '#00aeef',
                                color: currentPage === 1 ? '#999' : 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                fontWeight: '600'
                            }}
                        >
                            Previous
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                            if (
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 2 && page <= currentPage + 2)
                            ) {
                                return (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            background: page === currentPage ? '#344ea1' : '#00aeef',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontWeight: '600',
                                            minWidth: '40px'
                                        }}
                                    >
                                        {page}
                                    </button>
                                );
                            } else if (page === currentPage - 3 || page === currentPage + 3) {
                                return <span key={page} style={{ padding: '0.5rem' }}>...</span>;
                            }
                            return null;
                        })}

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            style={{
                                padding: '0.5rem 1rem',
                                background: currentPage === totalPages ? '#ddd' : '#00aeef',
                                color: currentPage === totalPages ? '#999' : 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                fontWeight: '600'
                            }}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default StockPage;

